import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, User, Mail, Lock, Save, Building2, Shield, CreditCard, Phone, MapPin, FileText, Bell, Plus, X } from "lucide-react";
import AppShell from "@/components/layout/AppShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, organization, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // Organization contact info
  const [orgPhone, setOrgPhone] = useState(organization?.phone || "");
  const [orgAddress, setOrgAddress] = useState(organization?.address || "");
  const [notificationPhones, setNotificationPhones] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);

  // Load notification phones from org contact info
  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const res = await axios.get(`${API}/organizations/contact-info`);
        if (res.data.notificationPhones) {
          setNotificationPhones(res.data.notificationPhones);
        }
      } catch (_) {}
    };
    if (user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner') {
      loadContactInfo();
    }
  }, [user?.role]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword && !currentPassword) {
      toast.error("Current password is required to change password");
      return;
    }

    setLoading(true);
    try {
      const updateData = {};
      
      if (name !== user?.name) {
        updateData.name = name;
      }
      
      if (email !== user?.email) {
        updateData.email = email;
      }
      
      if (phone !== user?.phone) {
        updateData.phone = phone;
      }
      
      if (newPassword) {
        updateData.currentPassword = currentPassword;
        updateData.newPassword = newPassword;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info("No changes to save");
        setLoading(false);
        return;
      }

      await axios.put(`${API}/auth/profile`, updateData);
      toast.success("Profile updated successfully!");
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
    setLoading(false);
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    
    // Only owners can update organization info
    if (user?.role !== 'owner' && user?.role?.toLowerCase() !== 'co-owner') {
      toast.error("Only owners can update organization information");
      return;
    }

    setOrgLoading(true);
    try {
      const updateData = {
        phone: orgPhone.trim(),
        address: orgAddress.trim()
      };

      await axios.put(`${API}/organizations/contact-info`, updateData);
      toast.success("Organization contact info updated! This will now appear on your reports.");
      
      // Refresh user data to get updated organization
      if (refreshUser) {
        await refreshUser();
      }
      
    } catch (error) {
      console.error("Error updating organization:", error);
      toast.error(error.response?.data?.detail || "Failed to update organization info");
    }
    setOrgLoading(false);
  };

  const handleSaveNotificationPhones = async () => {
    setSmsLoading(true);
    try {
      const cleaned = notificationPhones.filter(p => p.phone.trim());
      await axios.put(`${API}/organizations/contact-info`, { notificationPhones: cleaned });
      toast.success("Notification numbers saved. SMS will be sent when a center message is posted.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save notification numbers");
    }
    setSmsLoading(false);
  };

  const addNotificationPhone = () => {
    if (notificationPhones.length >= 5) {
      toast.error("Maximum 5 notification numbers allowed");
      return;
    }
    setNotificationPhones([...notificationPhones, { phone: "", name: "", enabled: true }]);
  };

  const removeNotificationPhone = (index) => {
    setNotificationPhones(notificationPhones.filter((_, i) => i !== index));
  };

  const updateNotificationPhoneField = (index, field, value) => {
    const updated = [...notificationPhones];
    updated[index] = { ...updated[index], [field]: value };
    setNotificationPhones(updated);
  };

  // Get plan display name
  const getPlanDisplay = () => {
    const plan = organization?.plan || 'signature'; // Default to signature to prevent lockout display issues
    const status = organization?.status || 'active';
    if (status === 'trial') return 'Free Trial';
    if (status === 'expired') return 'Trial Ended';
    if (status === 'cancelled') return 'Cancelled';
    if (plan === 'signature') return 'Signature Plan';
    return plan.charAt(0).toUpperCase() + plan.slice(1) + ' Plan';
  };

  return (
    <AppShell pageTitle="Edit Profile" breadcrumb={true}>
      <div className="max-w-2xl mx-auto">
        {/* Back / Close button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-sm font-medium text-ink-muted hover:text-brand-primary hover:bg-brand-primary/5 transition-colors"
          data-testid="profile-close-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        {/* Profile Header Card */}
        <Card className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white mb-6 border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{user?.name || 'User'}</h2>
                <p className="text-white/80">{user?.email}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm capitalize">
                    {user?.role || 'Staff'}
                  </span>
                  <span className="px-3 py-1 bg-brand-highlight/80 rounded-full text-sm">
                    {getPlanDisplay()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Info Card */}
        <Card className="bg-white border border-line shadow-sm mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-ink-main flex items-center gap-2">
              <Building2 className="w-5 h-5 text-brand-primary" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-muted">Center Name</span>
                <span className="font-medium text-ink-main">{organization?.name || 'ToDays Kidz Childcare Center'}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-line">
                <span className="text-ink-muted">Your Role</span>
                <span className="font-medium text-ink-main capitalize">{user?.role || 'Staff'}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-ink-muted">Subscription</span>
                <span className="font-medium text-brand-highlight">{getPlanDisplay()}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Contact Info + SMS Notifications - Owners only */}
        {(user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner') && (
          <>
          <Card className="bg-white border border-line shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-ink-main flex items-center gap-2">
                <MapPin className="w-5 h-5 text-brand-primary" />
                Organization Contact Info
              </CardTitle>
              <CardDescription className="flex items-start gap-2 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-blue-700 text-sm">
                  <strong>Tip:</strong> Add your center's phone number and address here. This information will appear on printed reports (Emergency Contacts, Attendance, etc.) right below your organization name, giving your reports a professional look.
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateOrganization} className="space-y-4">
                {/* Phone Number */}
                <div>
                  <Label htmlFor="orgPhone" className="text-ink-main font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-brand-primary" />
                    Center Phone Number
                  </Label>
                  <Input
                    id="orgPhone"
                    type="tel"
                    value={orgPhone}
                    onChange={(e) => setOrgPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1 border-line"
                  />
                </div>

                {/* Address */}
                <div>
                  <Label htmlFor="orgAddress" className="text-ink-main font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-brand-primary" />
                    Center Address
                  </Label>
                  <Textarea
                    id="orgAddress"
                    value={orgAddress}
                    onChange={(e) => setOrgAddress(e.target.value)}
                    placeholder="123 Main Street&#10;City, State 12345"
                    className="mt-1 border-line resize-none"
                    rows={2}
                  />
                </div>

                {/* Preview */}
                {(orgPhone || orgAddress) && (
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Preview on reports:</p>
                    <div className="text-center">
                      <p className="font-bold text-gray-800">{organization?.name}</p>
                      {orgAddress && <p className="text-sm text-gray-600 whitespace-pre-line">{orgAddress}</p>}
                      {orgPhone && <p className="text-sm text-gray-600">{orgPhone}</p>}
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={orgLoading}
                  className="w-full bg-brand-primary hover:bg-brand-primary/90"
                >
                  {orgLoading ? (
                    "Saving..."
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Contact Info
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* SMS Notification Numbers */}
          <Card className="border-line shadow-sm mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-ink-main flex items-center gap-2 text-base">
                <Bell className="w-5 h-5 text-brand-primary" />
                Center Message SMS Notifications
              </CardTitle>
              <CardDescription>
                <span className="text-blue-700 text-sm">
                  <strong>Premium+:</strong> When a center message is posted, these numbers receive a text automatically. Set once — no extra step when posting.
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {notificationPhones.map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 shrink-0" title={entry.enabled === false ? "Off — will not receive alerts" : "On — receives alerts"}>
                    <Switch
                      checked={entry.enabled !== false}
                      onCheckedChange={(checked) => updateNotificationPhoneField(index, "enabled", checked)}
                      data-testid={`notification-phone-enabled-${index}`}
                    />
                  </div>
                  <Input
                    type="tel"
                    value={entry.phone}
                    onChange={(e) => updateNotificationPhoneField(index, "phone", e.target.value)}
                    placeholder="4121234567"
                    className={`border-line flex-1 ${entry.enabled === false ? "opacity-50" : ""}`}
                    data-testid={`notification-phone-${index}`}
                  />
                  <Input
                    type="text"
                    value={entry.name}
                    onChange={(e) => updateNotificationPhoneField(index, "name", e.target.value)}
                    placeholder="Name"
                    className={`border-line w-32 ${entry.enabled === false ? "opacity-50" : ""}`}
                    data-testid={`notification-phone-name-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeNotificationPhone(index)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                    data-testid={`remove-notification-phone-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {notificationPhones.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addNotificationPhone}
                  className="w-full border-dashed border-brand-primary text-brand-primary hover:bg-brand-primary/5"
                  data-testid="add-notification-phone-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add number
                </Button>
              )}

              {notificationPhones.length > 0 && (
                <Button
                  type="button"
                  onClick={handleSaveNotificationPhones}
                  disabled={smsLoading}
                  className="w-full bg-brand-primary hover:bg-brand-primary/90"
                  data-testid="save-notification-phones-btn"
                >
                  {smsLoading ? "Saving..." : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Notification Numbers
                    </>
                  )}
                </Button>
              )}

              <p className="text-xs text-gray-600 font-medium">
                Not available on Basic Plan — requires Premium plan + Parent Communication subscription. Up to 10 numbers.
              </p>
              <p className="text-xs text-amber-600">
                Each recipient counts as a separate SMS toward your usage and any applicable charges.
              </p>
            </CardContent>
          </Card>
          </>
        )}

        {/* Edit Profile Form */}
        <Card className="bg-white border border-line shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-ink-main flex items-center gap-2">
              <User className="w-5 h-5 text-brand-primary" />
              Profile Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-ink-main font-medium flex items-center gap-2">
                  <User className="w-4 h-4 text-brand-primary" />
                  Display Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="border-line mt-2"
                  data-testid="profile-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-ink-main font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-brand-primary" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="border-line mt-2"
                  data-testid="profile-email-input"
                />
              </div>

              {/* Cell Phone — same number first entered at sign-up; used for your own SMS reminders
                  (e.g. Tour Planner's "Organization Owner" alert) and can be updated here any time. */}
              <div>
                <Label htmlFor="phone" className="text-ink-main font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-primary" />
                  Your Cell Phone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="border-line mt-2"
                  data-testid="profile-phone-input"
                />
                <p className="text-xs text-ink-muted mt-1">Used for your own SMS reminders, like Tour Planner alerts.</p>
              </div>

              {/* Change Password Section */}
              <div className="border-t border-line pt-6">
                <h3 className="text-lg font-semibold text-ink-main flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-brand-secondary" />
                  Change Password
                </h3>
                <p className="text-sm text-ink-muted mb-4">Leave blank to keep current password</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword" className="text-ink-main">
                      Current Password
                    </Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className="border-line mt-2"
                      data-testid="profile-current-password"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="newPassword" className="text-ink-main">
                      New Password
                    </Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="border-line mt-2"
                      data-testid="profile-new-password"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword" className="text-ink-main">
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="border-line mt-2"
                      data-testid="profile-confirm-password"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white rounded-xl"
                data-testid="profile-save-btn"
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
};

export default ProfilePage;
